import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom-v5-compat';

import FormAuthEnforcement, { FormAuthEnforcementData } from 'dashboard/forms/FormAuthEnforcement';
import { StartEnforcementValue } from 'dashboard/forms/FormAuthEnforcement/FormAuthEnforcement';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { User, UserStatus } from 'dashboard/models/user';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { Iso8601DateString } from 'lib/types';
import { assertNever } from 'lib/utils/types';

interface MfaRequirementModalProps {
  users: User[];
  currentUser: User;
  handleClose: () => void;
  handleSubmit: (values: Iso8601DateString) => Promise<void>;
}

/**
 * This can be tested via the fixture at spec/fixtures/saml/idp_metadata.xml.
 */
export default ({ users, currentUser, handleClose, handleSubmit }: MfaRequirementModalProps) => {
  const usersWithoutMfa = useMemo(
    () =>
      users.filter(
        ({ attributes: { status, 'available-mfa-methods': availableMfaMethods } }) =>
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          status === UserStatus.Active && availableMfaMethods.length === 0,
      ).length,
    [],
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const currentUserMfaNotEnabled = currentUser.attributes['available-mfa-methods'].length === 0;
  const currentUserAdminServiceAccount =
    currentUser?.attributes?.['email-address']?.startsWith('persona-admin+') &&
    currentUser?.attributes?.['email-address']?.endsWith('@withpersona.com');

  const initialValues = useMemo(
    () => ({
      startEnforcement: StartEnforcementValue.Immediately,
      fromDate: DateTime.local().plus({ month: 1 }).toISO(),
    }),
    [],
  );

  const handleEnableMfaRequirement = useCallback(
    async ({ startEnforcement, fromDate }: FormAuthEnforcementData) => {
      switch (startEnforcement) {
        case StartEnforcementValue.Immediately:
          await handleSubmit(new Date().toISOString());
          handleClose();
          break;
        case StartEnforcementValue.FromDate:
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          await handleSubmit(DateTime.fromISO(fromDate).toISO());
          handleClose();
          break;
        default:
          assertNever(startEnforcement);
      }
    },
    [],
  );

  return (
    <ModalContainer data-test='mfa-requirement-modal'>
      <ModalHeader data-test='mfa-requirement-modal__header' onClose={handleClose}>
        Enforce 2-factor authentication for your whole organization
      </ModalHeader>

      <ModalBody>
        {currentUserMfaNotEnabled && !currentUserAdminServiceAccount ? (
          <Paragraph data-test='mfa-require-modal__setup-mfa-message'>
            Please{' '}
            <Link to='/dashboard/user/profile/security'>
              set up 2-factor authentication for your own account
            </Link>{' '}
            before enforcing it for your organization.
          </Paragraph>
        ) : (
          <>
            <Paragraph>
              <LockOutWarningMessage lockedOutCount={usersWithoutMfa} />
            </Paragraph>
            <Spacer />
            <FormAuthEnforcement
              initialValues={initialValues}
              onSubmit={handleEnableMfaRequirement}
            />
          </>
        )}
      </ModalBody>
    </ModalContainer>
  );
};

const LockOutWarningMessage = ({ lockedOutCount }: { lockedOutCount: number }) => {
  switch (lockedOutCount) {
    case 0:
      return (
        <>Users without 2-factor authentication enabled will be locked out of their accounts.</>
      );
    case 1:
      return (
        <>
          <Link to='/dashboard/users'>1 user</Link> without 2-factor authentication will be locked
          out of their account. You will need to generate a one-time recovery code for them to
          regain access to their account.
        </>
      );
    default:
      return (
        <>
          <Link to='/dashboard/users'>{lockedOutCount} users</Link> without 2-factor authentication
          will be locked out of their accounts. You will need to generate a one-time recovery code
          for them to regain access to their accounts.
        </>
      );
  }
};
